/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/observability/error-telemetry.ts
 * layer: web
 * area: lib
 * kind: lib
 * web_root: lib
 * relpath: web/src/lib/error-telemetry.ts
 * filename: error-telemetry.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

/**
 * Frontend error telemetry module.
 *
 * Captures window errors, unhandled rejections, console.error calls, and
 * manual captureError/captureComponentError invocations.  Errors are batched
 * and flushed to POST /api/v1/telemetry/errors every 10 seconds or when
 * the batch reaches 20 entries.  The same message is sent at most 3 times
 * per session to avoid noise.
 */

// The API composition root injects the authenticated transport. Keeping this
// Keeping this module independent of the platform API client prevents telemetry from importing the
// client that already depends on telemetry (and avoids an instrumented request
// recursively constructing its own transport).

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorEntry {
  source: 'frontend';
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  url: string;
  component?: string;
  extra?: Record<string, unknown>;
  timestamp: string;
  userAgent: string;
}

// Build + session identity, attached to every entry via `extra` so errors can
// be correlated to a specific deploy and a single user's session in Grafana.
// Without the release you cannot tell a regression introduced by a deploy from
// pre-existing noise; without the session you cannot tell 50 errors from one
// broken session apart from 50 affected users.
const RELEASE = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown';

const SESSION_ID = (() => {
  try {
    const KEY = 'hyperion:telemetry-session';
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    // sessionStorage can throw in private modes / sandboxed iframes.
    return 'no-session';
  }
})();

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const BATCH_LIMIT = 20;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_DUPES_PER_SESSION = 3;
const TELEMETRY_PATH = '/telemetry/errors';

let initialized = false;
let queue: ErrorEntry[] = [];
let _flushTimer: ReturnType<typeof setInterval> | null = null;
const dupeCount = new Map<string, number>();
let originalConsoleError: (typeof console)['error'] | null = null;
let originalConsoleWarn: (typeof console)['warn'] | null = null;
export interface ErrorTelemetryBatch {
  errors: ErrorEntry[];
}

type TelemetryTransport = (path: string, body: ErrorTelemetryBatch) => Promise<void>;
let telemetryTransport: TelemetryTransport | null = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dedupeKey(message: string): string {
  // Normalise whitespace so minor formatting differences don't defeat dedup
  return message.replace(/\s+/g, ' ').trim().slice(0, 256);
}

const SENSITIVE_KEY = /authorization|cookie|credential|password|secret|token|api[_-]?key/i;

/**
 * Remove values that must never cross the browser telemetry boundary.
 *
 * Auth callback links and portal magic links legitimately place short-lived
 * credentials in the query string. Recording `window.location.href`, an Axios
 * URL, or a warning containing one of those links would turn observability into
 * a credential sink, so query/hash values and common bearer/key forms are
 * stripped before an entry is queued.
 */
function sanitizeText(value: string): string {
  return value
    .replace(/([?&](?:[^\s=&#]+)=)[^\s&#]*/g, '$1[REDACTED]')
    .replace(/(bearer\s+)[a-z0-9._~+/=-]+/gi, '$1[REDACTED]')
    .replace(/((?:password|secret|token|api[_-]?key)\s*[:=]\s*)[^\s,;]+/gi, '$1[REDACTED]');
}

function sanitizeExtra(value: unknown, depth = 0): unknown {
  if (depth > 4) return '[TRUNCATED]';
  if (typeof value === 'string') return sanitizeText(value);
  if (Array.isArray(value)) return value.slice(0, 50).map((item) => sanitizeExtra(item, depth + 1));
  if (value && typeof value === 'object') {
    const safe: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value).slice(0, 50)) {
      safe[key] = SENSITIVE_KEY.test(key) ? '[REDACTED]' : sanitizeExtra(item, depth + 1);
    }
    return safe;
  }
  return value;
}

function pageUrl(): string {
  try {
    return `${window.location.origin}${window.location.pathname}`;
  } catch {
    return 'unknown';
  }
}

function shouldCapture(message: string): boolean {
  const key = dedupeKey(message);
  const count = dupeCount.get(key) ?? 0;
  if (count >= MAX_DUPES_PER_SESSION) return false;
  dupeCount.set(key, count + 1);
  return true;
}

function buildEntry(
  message: string,
  opts?: {
    level?: 'error' | 'warn';
    stack?: string;
    component?: string;
    extra?: Record<string, unknown>;
  }
): ErrorEntry {
  return {
    source: 'frontend',
    level: opts?.level ?? 'error',
    message: sanitizeText(message),
    stack: opts?.stack ? sanitizeText(opts.stack) : undefined,
    url: pageUrl(),
    component: opts?.component,
    extra: sanitizeExtra({ ...opts?.extra, release: RELEASE, session_id: SESSION_ID }) as Record<
      string,
      unknown
    >,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
  };
}

function enqueue(entry: ErrorEntry): void {
  queue.push(entry);
  if (queue.length >= BATCH_LIMIT) {
    flush();
  }
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  if (!telemetryTransport) return;

  const batch = queue;
  queue = [];

  try {
    await telemetryTransport(TELEMETRY_PATH, { errors: batch });
  } catch {
    // Silently drop -- never create an error loop
  }
}

/**
 * Flush synchronously during page teardown.
 *
 * The normal flush() awaits a dynamic import and then an XHR; on
 * pagehide/visibilitychange the browser routinely kills both, so the errors
 * most worth having — the ones immediately before a crash, reload or
 * navigation — were the ones being dropped. sendBeacon hands the payload to
 * the browser, which delivers it independently of the page's lifetime.
 *
 * Falls back to the async path when sendBeacon is unavailable or refuses the
 * payload (it enforces its own size limit).
 */
function flushOnUnload(): void {
  if (queue.length === 0) return;

  const batch = queue;

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // Same-origin path; the CSP allows connect-src 'self'. Must match the
      // axios baseURL so it lands on the same authenticated route (cookies are
      // sent because withCredentials is the default for beacons).
      const base = import.meta.env.VITE_API_URL || '/api/v1';
      const body = new Blob([JSON.stringify({ errors: batch })], {
        type: 'application/json',
      });
      if (navigator.sendBeacon(`${base}${TELEMETRY_PATH}`, body)) {
        queue = [];
        return;
      }
    }
  } catch {
    // fall through to the async path
  }

  // sendBeacon unavailable or rejected the payload — best-effort async flush.
  flush();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Install the authenticated API transport without creating a lib -> api dependency. */
export function configureErrorTelemetryTransport(transport: TelemetryTransport): void {
  telemetryTransport = transport;
}

/**
 * Manually capture an error (e.g. inside a catch block).
 */
export function captureError(error: Error | string, context?: Record<string, unknown>): void {
  try {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;

    if (!shouldCapture(message)) return;
    enqueue(buildEntry(message, { stack, extra: context }));
  } catch {
    // Never throw from telemetry
  }
}

/**
 * Record a non-error product signal that needs to be countable in Grafana.
 *
 * Emitted at warn level so it lands in Loki via the same pipeline as errors
 * without inflating error counts (the dashboards and alerts filter on
 * level="error").
 *
 * Used for questions that can only be answered by observing real traffic —
 * chiefly "is this legacy route still being used, or can it be retired?".
 */
export function captureSignal(name: string, context?: Record<string, unknown>): void {
  try {
    // Signals are low-volume and deliberately exempt from the duplicate cap:
    // the whole point is to count every occurrence over time.
    enqueue(buildEntry(name, { level: 'warn', extra: context }));
  } catch {
    // Never throw from telemetry
  }
}

/**
 * Capture a React component render error (for use inside componentDidCatch).
 */
export function captureComponentError(error: Error, componentName: string): void {
  try {
    if (!shouldCapture(error.message)) return;
    enqueue(
      buildEntry(error.message, {
        stack: error.stack,
        component: componentName,
      })
    );
  } catch {
    // Never throw from telemetry
  }
}

/**
 * Initialise global error handlers.  Call once before ReactDOM.createRoot().
 */
export function initErrorTelemetry(): void {
  if (initialized) return;
  initialized = true;

  // --- window.onerror ---
  try {
    const prevOnError = window.onerror;
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ) => {
      try {
        const msg = typeof message === 'string' ? message : 'Unknown window error';
        if (shouldCapture(msg)) {
          enqueue(
            buildEntry(msg, {
              stack: error?.stack,
              extra: { source, lineno, colno },
            })
          );
        }
      } catch {
        // swallow
      }
      if (typeof prevOnError === 'function') {
        prevOnError(message, source, lineno, colno, error);
      }
    };
  } catch {
    // swallow
  }

  // --- unhandled promise rejections ---
  try {
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const msg =
          // error-feedback-ok: telemetry records the raw rejection, not user copy.
          reason instanceof Error
            ? reason.message
            : typeof reason === 'string'
              ? reason
              : 'Unhandled promise rejection';
        const stack = reason instanceof Error ? reason.stack : undefined;
        if (shouldCapture(msg)) {
          enqueue(buildEntry(msg, { stack }));
        }
      } catch {
        // swallow
      }
    });
  } catch {
    // swallow
  }

  // --- monkey-patch console.error ---
  try {
    originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      try {
        const msg = args
          .map((a) => {
            if (a instanceof Error) return a.message;
            if (typeof a === 'string') return a;
            try {
              return JSON.stringify(a);
            } catch {
              return String(a);
            }
          })
          .join(' ');

        if (shouldCapture(msg)) {
          const stack = args.find((a): a is Error => a instanceof Error)?.stack;
          enqueue(buildEntry(msg, { level: 'error', stack }));
        }
      } catch {
        // swallow
      }

      // Always call the original so dev‐tools still work
      originalConsoleError?.apply(console, args);
    };
  } catch {
    // swallow
  }

  // --- capture actionable console warnings ---
  try {
    originalConsoleWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      try {
        const message = args
          .map((argument) => {
            if (argument instanceof Error) return argument.message;
            if (typeof argument === 'string') return argument;
            try {
              return JSON.stringify(argument);
            } catch {
              return String(argument);
            }
          })
          .join(' ');

        if (shouldCapture(message)) {
          const stack = args.find((argument): argument is Error => argument instanceof Error)?.stack;
          enqueue(buildEntry(message, { level: 'warn', stack }));
        }
      } catch {
        // swallow
      }

      originalConsoleWarn?.apply(console, args);
    };
  } catch {
    // swallow
  }

  // --- periodic flush ---
  try {
    _flushTimer = setInterval(() => {
      flush();
    }, FLUSH_INTERVAL_MS);

    // Flush on page teardown via sendBeacon — see flushOnUnload().
    // `pagehide` is the reliable signal (beforeunload does not fire on mobile
    // Safari and is skipped by the bfcache); visibilitychange covers tab
    // switches and app backgrounding.
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushOnUnload();
      }
    });
    window.addEventListener('pagehide', () => {
      flushOnUnload();
    });
  } catch {
    // swallow
  }
}
